import type { ReactNode } from 'react';
import { ArrowUpRight } from 'lucide-react';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { RICH_TEXT_HTML_CLASS } from '~/ui-rocco/pages/detail-shared';
import { ACHIPRO_PHOTOGRAPHER_SPLIT } from '@modules/project/component/details/util/get-details-items';

/** One spec-table row, as produced by `getDetailsItems`. */
export interface ProjectSpecItem {
    itemName: string;
    itemNameDesktop?: string;
    value: string;
    link?: string;
}

interface AboutProjectRowContentProps {
    /** Large lead statement — the project's editorial subheading. */
    heading?: string;
    /** Project description as CMS rich-text HTML. */
    contentHtml?: string;
    /** Key/value spec rows (Title / Builder / Category / Completed / …). */
    specItems?: ProjectSpecItem[];
}

// Dark-theme twin of the product page's (01) spec table (ProductDetailsRowContent):
// medium label left, grey value right, a hairline under every row.
const ROW_CLASS =
    'flex items-start border-b border-white/10 first:pt-0 last:border-b-0 max-md:gap-4 max-md:py-2 md:gap-[3.0625rem] md:py-[0.8125rem]';
const LABEL_CLASS =
    'shrink-0 font-sans font-medium leading-[1.3] whitespace-pre-line text-white max-md:w-[7.5rem] max-md:text-sm md:w-[16.1875rem] md:text-[1.375rem]';
/** Text track + fixed arrow track so unlinked rows align with text, not icons. */
const VALUE_CLASS =
    'm-0 grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_0.85em] items-baseline gap-1 break-words text-right font-sans font-normal leading-[1.3] text-[#a3a3a3] max-md:text-sm md:text-[1.375rem]';
const VALUE_LINE_CLASS =
    'grid w-full grid-cols-[minmax(0,1fr)_0.85em] items-baseline gap-1';
const LINK_CLASS =
    'underline decoration-white/25 underline-offset-2 transition-colors hover:text-white';

/** Grey underline alone is too subtle on the grey value text, so linked values
 *  carry a small arrow (matches the product spec table). Kept in a fixed column
 *  so rows without a link still line up with the text above. */
const LinkArrow = ({ show }: { show: boolean }) => (
    <span
        className="inline-flex size-[0.85em] shrink-0 items-center justify-center"
        aria-hidden={!show}
    >
        {show ? (
            <ArrowUpRight
                aria-hidden="true"
                strokeWidth={2}
                className="size-[0.85em] -translate-y-[0.05em]"
            />
        ) : null}
    </span>
);

/**
 * Renders a spec value: the category crumbs as "/"-joined links, the
 * photographers as stacked (per-person) links, and any other linked value as a
 * single link — all with the shared underline + arrow affordance.
 */
const SpecValue = ({ item }: { item: ProjectSpecItem }): ReactNode => {
    if (item.itemName === 'Category' && item.link) {
        const crumbs = item.value.split(',');
        const links = item.link.split(',');
        return (
            <>
                <span className="min-w-0 text-right">
                    {crumbs.map((crumb, index) => {
                        const to = links[index];
                        return (
                            <span key={`${crumb}-${index}`}>
                                {index > 0 ? (
                                    <span className="text-white/40"> / </span>
                                ) : null}
                                {to ? (
                                    <RoccoLink to={to} className={LINK_CLASS}>
                                        {crumb}
                                    </RoccoLink>
                                ) : (
                                    crumb
                                )}
                            </span>
                        );
                    })}
                </span>
                <LinkArrow show />
            </>
        );
    }

    if (item.itemName === 'Photographers') {
        const names = item.value.split(ACHIPRO_PHOTOGRAPHER_SPLIT);
        const links = (item.link ?? '').split(ACHIPRO_PHOTOGRAPHER_SPLIT);
        return (
            <span className="col-span-2 flex flex-col">
                {names.map((name, index) => {
                    const to = links[index];
                    return to ? (
                        <RoccoLink
                            key={`${name}-${index}`}
                            to={to}
                            className={cn(LINK_CLASS, VALUE_LINE_CLASS)}
                        >
                            <span className="min-w-0 text-right">{name}</span>
                            <LinkArrow show />
                        </RoccoLink>
                    ) : (
                        <span
                            key={`${name}-${index}`}
                            className={VALUE_LINE_CLASS}
                        >
                            <span className="min-w-0 text-right">{name}</span>
                            <LinkArrow show={false} />
                        </span>
                    );
                })}
            </span>
        );
    }

    if (item.link) {
        return (
            <RoccoLink
                to={item.link}
                className={cn(LINK_CLASS, 'col-span-2', VALUE_LINE_CLASS)}
            >
                <span className="min-w-0 text-right">{item.value}</span>
                <LinkArrow show />
            </RoccoLink>
        );
    }

    return (
        <>
            <span className="min-w-0 text-right">{item.value}</span>
            <LinkArrow show={false} />
        </>
    );
};

/**
 * (01) About the Project — the open state of the numbered accordion row. A large
 * lead statement over the project's description prose, then the project's spec
 * table (Title / Builder / Category / Completed / Price range / …) in the same
 * label-left, value-right layout as the product page's (01) Product Details.
 * Set in the right-hand editorial column per the June 2026 Figma (2354:59741).
 */
export const AboutProjectRowContent = ({
    heading,
    contentHtml,
    specItems = [],
}: AboutProjectRowContentProps) => {
    if (!heading && !contentHtml && specItems.length === 0) return null;

    return (
        <div className="max-md:pt-6 max-md:pb-4 md:pt-[7.5rem] md:pb-[5rem]">
            <div className="ml-auto w-full font-sans text-white md:mr-[8.45%] md:w-[49.6%]">
                {heading ? (
                    // Lead statement — Figma 2354:59745: 39px, 400, leading 1.3.
                    // Mobile drops to the 24px accordion-row heading scale.
                    <p className="m-0 leading-[1.3] font-normal max-md:text-[1.5rem] md:text-[2.4375rem]">
                        {heading}
                    </p>
                ) : null}
                {contentHtml ? (
                    <div
                        className={cn(
                            // Prose — Figma 2354:59747: 20px, 400, leading 1.3;
                            // mobile drops to the 16px body token.
                            'leading-[1.3] font-normal max-md:text-base md:text-[1.25rem]',
                            // heading→prose gap ≈ 92px (Figma 2354:59745/59747).
                            heading && 'max-md:mt-4 md:mt-[5.75rem]',
                            RICH_TEXT_HTML_CLASS,
                        )}
                        dangerouslySetInnerHTML={{ __html: contentHtml }}
                    />
                ) : null}
                {specItems.length > 0 ? (
                    <dl
                        className={cn(
                            'm-0',
                            (heading || contentHtml) &&
                                'max-md:mt-8 md:mt-[3.75rem]',
                        )}
                    >
                        {specItems.map((item, index) => (
                            <div
                                key={`${item.itemName}-${index}`}
                                className={ROW_CLASS}
                            >
                                <dt className={LABEL_CLASS}>
                                    {item.itemNameDesktop ?? item.itemName}
                                </dt>
                                <dd className={VALUE_CLASS}>
                                    <SpecValue item={item} />
                                </dd>
                            </div>
                        ))}
                    </dl>
                ) : null}
            </div>
        </div>
    );
};
